import {
  decomposeRegroupingWorkshopValue,
  validateRegroupingWorkshopConfig,
  type RegroupingWorkshopConfig,
  type RegroupingWorkshopPlaceCounts,
} from './RegroupingWorkshopConfig.js';

export type RegroupingWorkshopSource = 'a' | 'b';
export type RegroupingWorkshopTenSource = RegroupingWorkshopSource | 'regrouped';
export type RegroupingWorkshopPhase =
  | 'discovering'
  | 'combining-ones'
  | 'selecting-ten'
  | 'regrouping'
  | 'combining-tens'
  | 'ready-to-power'
  | 'complete';

export type RegroupingWorkshopActionResult =
  | 'accepted'
  | 'ready'
  | 'complete'
  | 'invalid'
  | 'source-hidden'
  | 'empty-source'
  | 'piece-not-found'
  | 'inspect-addends'
  | 'combine-ones-first'
  | 'not-enough-selected'
  | 'too-many-selected'
  | 'no-group-available'
  | 'regroup-required'
  | 'combine-tens-first'
  | 'check-total';

export const REGROUPING_WORKSHOP_FEEDBACK: Readonly<Record<
  Exclude<RegroupingWorkshopActionResult, 'accepted' | 'ready' | 'complete' | 'invalid'>,
  string
>> = {
  'source-hidden': 'Inspect that number first so you can see its tens and ones.',
  'empty-source': 'You moved all of those pieces already.',
  'piece-not-found': 'That one is not in the Ones Chamber anymore.',
  'inspect-addends': 'Inspect both numbers before powering the workshop.',
  'combine-ones-first': 'Combine all the ones in the Ones Chamber first.',
  'not-enough-selected': 'Not quite enough to make a new ten yet.',
  'too-many-selected': 'A new ten needs exactly 10 ones. Leave the extras in the chamber.',
  'no-group-available': 'There are not 10 ones available to regroup.',
  'regroup-required': 'Find exactly 10 ones and use the regroup mechanism.',
  'combine-tens-first': 'Move every ten, including the new ten, into the Tens Chamber.',
  'check-total': 'Check the tens and ones in the final chambers.',
};

export interface RegroupingWorkshopHint {
  readonly stage: 1 | 2 | 3 | 4;
  readonly focus: 'ones-chamber' | 'regroup-selection' | 'tens-chamber';
  readonly message: string;
}

export interface RegroupingWorkshopSerializedState {
  readonly version: 1;
  readonly configId: string;
  readonly revealedSources: readonly RegroupingWorkshopSource[];
  readonly sourceATensRemaining: number;
  readonly sourceAOnesRemaining: number;
  readonly sourceBTensRemaining: number;
  readonly sourceBOnesRemaining: number;
  readonly combinedOneIds: readonly string[];
  readonly selectedOneIds: readonly string[];
  readonly regroupedTens: number;
  readonly regroupedTensAvailable: number;
  readonly combinedTens: number;
  readonly hintStage: 0 | 1 | 2 | 3 | 4;
  readonly hint?: RegroupingWorkshopHint;
  readonly completed: boolean;
}

export interface RegroupingWorkshopSnapshot {
  readonly phase: RegroupingWorkshopPhase;
  readonly sourceA: RegroupingWorkshopPlaceCounts;
  readonly sourceB: RegroupingWorkshopPlaceCounts;
  readonly sourceARemaining: RegroupingWorkshopPlaceCounts;
  readonly sourceBRemaining: RegroupingWorkshopPlaceCounts;
  readonly revealedSources: readonly RegroupingWorkshopSource[];
  readonly combinedOneIds: readonly string[];
  readonly selectedOneIds: readonly string[];
  readonly combinedOnes: number;
  readonly groupsOfTenAvailable: number;
  readonly regroupedTens: number;
  readonly regroupedTensAvailable: number;
  readonly remainingOnes: number;
  readonly combinedTens: number;
  readonly finalNumber: number;
  readonly target: number;
  readonly hintStage: 0 | 1 | 2 | 3 | 4;
  readonly hint?: RegroupingWorkshopHint;
  readonly isComplete: boolean;
}

type MutableCounts = { tens: number; ones: number };

export class RegroupingWorkshopState {
  private readonly sourceAInitial: RegroupingWorkshopPlaceCounts;
  private readonly sourceBInitial: RegroupingWorkshopPlaceCounts;
  private sourceA: MutableCounts;
  private sourceB: MutableCounts;
  private readonly revealedSources = new Set<RegroupingWorkshopSource>();
  private combinedOneIds: string[] = [];
  private readonly selectedOneIds = new Set<string>();
  private regroupedTens = 0;
  private regroupedTensAvailable = 0;
  private combinedTens = 0;
  private hintStage: 0 | 1 | 2 | 3 | 4 = 0;
  private currentHint?: RegroupingWorkshopHint;
  private completed = false;

  constructor(
    readonly config: RegroupingWorkshopConfig,
    serialized?: RegroupingWorkshopSerializedState,
  ) {
    validateRegroupingWorkshopConfig(config);
    this.sourceAInitial = decomposeRegroupingWorkshopValue(config.addendA);
    this.sourceBInitial = decomposeRegroupingWorkshopValue(config.addendB);
    this.sourceA = { ...this.sourceAInitial };
    this.sourceB = { ...this.sourceBInitial };
    if (serialized) this.restore(serialized);
  }

  revealSource(source: RegroupingWorkshopSource): RegroupingWorkshopActionResult {
    if (this.completed || this.revealedSources.has(source)) return 'invalid';
    this.revealedSources.add(source);
    this.clearHint();
    return 'accepted';
  }

  combineOne(source: RegroupingWorkshopSource, amount = 1): RegroupingWorkshopActionResult {
    if (!this.canMove(amount)) return 'invalid';
    if (!this.revealedSources.has(source)) return 'source-hidden';
    const counts = source === 'a' ? this.sourceA : this.sourceB;
    if (counts.ones < amount) return 'empty-source';

    const movedBefore = (source === 'a' ? this.sourceAInitial.ones : this.sourceBInitial.ones)
      - counts.ones;
    for (let index = 1; index <= amount; index += 1) {
      this.combinedOneIds.push(`${source}-one-${movedBefore + index}`);
    }
    counts.ones -= amount;
    this.clearHint();
    return this.phase === 'selecting-ten' ? 'ready' : 'accepted';
  }

  toggleOneSelection(pieceId: string): RegroupingWorkshopActionResult {
    if (this.completed || this.sourceA.ones + this.sourceB.ones > 0) return 'combine-ones-first';
    if (!this.combinedOneIds.includes(pieceId)) return 'piece-not-found';
    if (this.selectedOneIds.has(pieceId)) this.selectedOneIds.delete(pieceId);
    else this.selectedOneIds.add(pieceId);
    this.clearHint();
    return this.selectedOneIds.size === 10 ? 'ready' : 'accepted';
  }

  clearSelection(): RegroupingWorkshopActionResult {
    if (this.completed || this.selectedOneIds.size === 0) return 'invalid';
    this.selectedOneIds.clear();
    this.clearHint();
    return 'accepted';
  }

  regroupSelectedOnes(): RegroupingWorkshopActionResult {
    if (this.completed) return 'invalid';
    if (this.sourceA.ones + this.sourceB.ones > 0) return 'combine-ones-first';
    if (this.groupsOfTenAvailable < 1) return 'no-group-available';
    if (this.selectedOneIds.size < 10) return 'not-enough-selected';
    if (this.selectedOneIds.size > 10) return 'too-many-selected';

    this.combinedOneIds = this.combinedOneIds.filter(id => !this.selectedOneIds.has(id));
    this.selectedOneIds.clear();
    this.regroupedTens += 1;
    this.regroupedTensAvailable += 1;
    this.clearHint();
    return 'accepted';
  }

  combineTen(source: RegroupingWorkshopTenSource, amount = 1): RegroupingWorkshopActionResult {
    if (!this.canMove(amount)) return 'invalid';
    if (this.revealedSources.size < 2) return 'inspect-addends';
    if (this.sourceA.ones + this.sourceB.ones > 0) return 'combine-ones-first';
    if (this.combinedOneIds.length >= 10) return 'regroup-required';

    if (source === 'regrouped') {
      if (this.regroupedTensAvailable < amount) return 'empty-source';
      this.regroupedTensAvailable -= amount;
    } else {
      if (!this.revealedSources.has(source)) return 'source-hidden';
      const counts = source === 'a' ? this.sourceA : this.sourceB;
      if (counts.tens < amount) return 'empty-source';
      counts.tens -= amount;
    }
    this.combinedTens += amount;
    this.clearHint();
    return this.phase === 'ready-to-power' ? 'ready' : 'accepted';
  }

  powerWorkshop(): RegroupingWorkshopActionResult {
    if (this.completed) return 'complete';
    if (this.revealedSources.size < 2) return 'inspect-addends';
    if (this.sourceA.ones + this.sourceB.ones > 0) return 'combine-ones-first';
    if (this.combinedOneIds.length >= 10) return 'regroup-required';
    if (this.sourceA.tens + this.sourceB.tens + this.regroupedTensAvailable > 0) {
      return 'combine-tens-first';
    }
    if (!this.matchesTarget) return 'check-total';

    this.completed = true;
    this.clearHint();
    return 'complete';
  }

  requestHint(): RegroupingWorkshopHint {
    this.hintStage = Math.min(4, this.hintStage + 1) as 1 | 2 | 3 | 4;
    const messages: Record<1 | 2 | 3 | 4, RegroupingWorkshopHint> = {
      1: {
        stage: 1,
        focus: 'ones-chamber',
        message: 'Look at how many ones you have altogether.',
      },
      2: {
        stage: 2,
        focus: 'regroup-selection',
        message: 'Can you find a group of 10 ones?',
      },
      3: {
        stage: 3,
        focus: 'regroup-selection',
        message: 'Ten ones can become one new ten.',
      },
      4: {
        stage: 4,
        focus: this.regroupedTens > 0 ? 'tens-chamber' : 'regroup-selection',
        message: this.regroupedTens > 0
          ? 'Move the new ten and the other tens into the Tens Chamber.'
          : 'Focus on exactly 10 ones, then activate the regroup mechanism.',
      },
    };
    this.currentHint = messages[this.hintStage];
    return this.currentHint;
  }

  reset() {
    this.sourceA = { ...this.sourceAInitial };
    this.sourceB = { ...this.sourceBInitial };
    this.revealedSources.clear();
    this.combinedOneIds = [];
    this.selectedOneIds.clear();
    this.regroupedTens = 0;
    this.regroupedTensAvailable = 0;
    this.combinedTens = 0;
    this.hintStage = 0;
    this.currentHint = undefined;
    this.completed = false;
  }

  serialize(): RegroupingWorkshopSerializedState {
    return {
      version: 1,
      configId: this.config.id,
      revealedSources: [...this.revealedSources],
      sourceATensRemaining: this.sourceA.tens,
      sourceAOnesRemaining: this.sourceA.ones,
      sourceBTensRemaining: this.sourceB.tens,
      sourceBOnesRemaining: this.sourceB.ones,
      combinedOneIds: [...this.combinedOneIds],
      selectedOneIds: [...this.selectedOneIds],
      regroupedTens: this.regroupedTens,
      regroupedTensAvailable: this.regroupedTensAvailable,
      combinedTens: this.combinedTens,
      hintStage: this.hintStage,
      hint: this.currentHint,
      completed: this.completed,
    };
  }

  get phase(): RegroupingWorkshopPhase {
    if (this.completed) return 'complete';
    if (this.revealedSources.size < 2) return 'discovering';
    if (this.sourceA.ones + this.sourceB.ones > 0) return 'combining-ones';
    if (this.regroupedTens === 0) {
      return this.selectedOneIds.size === 10 ? 'regrouping' : 'selecting-ten';
    }
    if (this.sourceA.tens + this.sourceB.tens + this.regroupedTensAvailable > 0) {
      return 'combining-tens';
    }
    return 'ready-to-power';
  }

  get snapshot(): RegroupingWorkshopSnapshot {
    return {
      phase: this.phase,
      sourceA: { ...this.sourceAInitial },
      sourceB: { ...this.sourceBInitial },
      sourceARemaining: { ...this.sourceA },
      sourceBRemaining: { ...this.sourceB },
      revealedSources: [...this.revealedSources],
      combinedOneIds: [...this.combinedOneIds],
      selectedOneIds: [...this.selectedOneIds],
      combinedOnes: this.combinedOneIds.length,
      groupsOfTenAvailable: this.groupsOfTenAvailable,
      regroupedTens: this.regroupedTens,
      regroupedTensAvailable: this.regroupedTensAvailable,
      remainingOnes: this.combinedOneIds.length,
      combinedTens: this.combinedTens,
      finalNumber: this.combinedTens * 10 + this.combinedOneIds.length,
      target: this.config.target,
      hintStage: this.hintStage,
      hint: this.currentHint,
      isComplete: this.completed,
    };
  }

  private restore(serialized: RegroupingWorkshopSerializedState) {
    if (serialized.version !== 1 || serialized.configId !== this.config.id) {
      throw new RangeError('Regrouping Workshop snapshot does not match this configuration.');
    }
    const counts = [
      serialized.sourceATensRemaining,
      serialized.sourceAOnesRemaining,
      serialized.sourceBTensRemaining,
      serialized.sourceBOnesRemaining,
      serialized.regroupedTens,
      serialized.regroupedTensAvailable,
      serialized.combinedTens,
      serialized.hintStage,
    ];
    if (counts.some(value => !Number.isInteger(value) || value < 0)) {
      throw new RangeError('Regrouping Workshop snapshot counts must be non-negative whole numbers.');
    }
    if (serialized.hintStage > 4) {
      throw new RangeError('Regrouping Workshop snapshot hint stage is invalid.');
    }
    if (serialized.sourceATensRemaining > this.sourceAInitial.tens
      || serialized.sourceAOnesRemaining > this.sourceAInitial.ones
      || serialized.sourceBTensRemaining > this.sourceBInitial.tens
      || serialized.sourceBOnesRemaining > this.sourceBInitial.ones) {
      throw new RangeError('Regrouping Workshop snapshot source counts exceed the configuration.');
    }

    const revealed = [...new Set(serialized.revealedSources)];
    if (revealed.length !== serialized.revealedSources.length
      || revealed.some(source => source !== 'a' && source !== 'b')) {
      throw new RangeError('Regrouping Workshop snapshot contains invalid revealed sources.');
    }
    const availableOneIds = new Set([
      ...Array.from({ length: this.sourceAInitial.ones }, (_, index) => `a-one-${index + 1}`),
      ...Array.from({ length: this.sourceBInitial.ones }, (_, index) => `b-one-${index + 1}`),
    ]);
    if (new Set(serialized.combinedOneIds).size !== serialized.combinedOneIds.length
      || serialized.combinedOneIds.some(id => !availableOneIds.has(id))
      || serialized.selectedOneIds.some(id => !serialized.combinedOneIds.includes(id))) {
      throw new RangeError('Regrouping Workshop snapshot contains invalid one-piece identifiers.');
    }
    if (new Set(serialized.selectedOneIds).size !== serialized.selectedOneIds.length) {
      throw new RangeError('Regrouping Workshop snapshot contains duplicate selected pieces.');
    }

    const initialOnes = this.sourceAInitial.ones + this.sourceBInitial.ones;
    const conservedOnes = serialized.sourceAOnesRemaining
      + serialized.sourceBOnesRemaining
      + serialized.combinedOneIds.length
      + serialized.regroupedTens * 10;
    const initialTens = this.sourceAInitial.tens + this.sourceBInitial.tens;
    const conservedTens = serialized.sourceATensRemaining
      + serialized.sourceBTensRemaining
      + serialized.regroupedTensAvailable
      + serialized.combinedTens;
    if (conservedOnes !== initialOnes || conservedTens !== initialTens + serialized.regroupedTens
      || serialized.regroupedTensAvailable > serialized.regroupedTens) {
      throw new RangeError('Regrouping Workshop snapshot does not conserve place-value pieces.');
    }

    this.sourceA = {
      tens: serialized.sourceATensRemaining,
      ones: serialized.sourceAOnesRemaining,
    };
    this.sourceB = {
      tens: serialized.sourceBTensRemaining,
      ones: serialized.sourceBOnesRemaining,
    };
    revealed.forEach(source => this.revealedSources.add(source));
    this.combinedOneIds = [...serialized.combinedOneIds];
    serialized.selectedOneIds.forEach(id => this.selectedOneIds.add(id));
    this.regroupedTens = serialized.regroupedTens;
    this.regroupedTensAvailable = serialized.regroupedTensAvailable;
    this.combinedTens = serialized.combinedTens;
    this.hintStage = serialized.hintStage;
    this.currentHint = serialized.hint;
    this.completed = serialized.completed;
    if (this.completed && !this.matchesTarget) {
      throw new RangeError('A completed Regrouping Workshop snapshot must match its target.');
    }
  }

  private canMove(amount: number) {
    return !this.completed && Number.isInteger(amount) && amount > 0;
  }

  private clearHint() {
    this.currentHint = undefined;
  }

  private get groupsOfTenAvailable() {
    return Math.floor(this.combinedOneIds.length / 10);
  }

  private get matchesTarget() {
    const target = decomposeRegroupingWorkshopValue(this.config.target);
    return this.combinedTens === target.tens && this.combinedOneIds.length === target.ones;
  }
}
